import { useState } from "react";
import { OKRESY_DNI, type OkresDni, type ZrodloKosztow } from "@shared/kosztyTypy.js";
import { TabelaEtapow, TabelaModeli } from "../components/koszty/TabelaKosztow.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Card } from "../components/ui/Card.js";
import { KpiGrid, KpiTile } from "../components/ui/KpiTile.js";
import { SegmentedControl } from "../components/ui/SegmentedControl.js";
import { StanBledu, StanLadowania, StanPusty } from "../components/ui/stany.js";
import { useKoszty } from "../hooks/useKoszty.js";
import { formatujLiczbe, kosztDoPokazania, opisKursu } from "../lib/koszty.js";
import { odmien } from "../lib/polski.js";

/**
 * Strona "Koszty": ile kosztowaly wywolania modeli w ostatnich 7, 30 albo 90 dniach, z podzialem na modele i etapy.
 * Standard z innych automatyzacji Urmate: okres, kafelki z liczbami, tabela per model. Tu dochodzi uczciwosc liczb:
 * koszt to SZACUNEK z cennika (cennik/cennik.json) i zuzycia z dziennika wywolan, brak danych to kreska z powodem,
 * a suma, ktorej czesci nie znamy, ma plakietke "niepelny" i prefiks "od".
 *
 * Kafelki to KpiTile na washu (siatka KpiGrid), nie w karcie (T-2). Bez trendu: KpiTile wymusza zielony wzrost, a wzrost
 * kosztu nie jest sukcesem. Wszystko z jednego zapytania, ze stanami (ladowanie, blad, pusto) w tych samych kartach (S-5).
 */

const OPCJE_OKRESU = OKRESY_DNI.map((d) => ({ wartosc: String(d), etykieta: `${d} dni` }));
const OPCJE_ZRODLA: readonly { wartosc: ZrodloKosztow; etykieta: string }[] = [
  { wartosc: "analizy", etykieta: "Analizy rozmów" },
  { wartosc: "mikrofon", etykieta: "Mikrofon" },
];

function dataPl(iso: string): string {
  return iso.slice(0, 10);
}

export function KosztyPage(): React.JSX.Element {
  const [dni, setDni] = useState<OkresDni>(30);
  const [zrodlo, setZrodlo] = useState<ZrodloKosztow>("analizy");
  const { dane: odebrane, ladowanie, blad, odswiez } = useKoszty(dni, zrodlo);
  // Odpowiedz z INNEGO okresu albo zrodla (poprzednie zapytanie) nie jest pokazywana pod nowymi etykietami: suma z 30
  // dni pod napisem "ostatnie 90 dni" to liczba, ktorej nie ufamy. Dopoki nie ma pasujacej odpowiedzi, jest szkielet,
  // a gdy pobranie padnie, stan bledu z ponowieniem (a nie cicha stara suma).
  const dane = odebrane !== null && odebrane.okres.dni === dni && odebrane.zrodlo === zrodlo ? odebrane : null;

  const podsumowanie = dane?.podsumowanie ?? null;
  const suma = podsumowanie?.suma ?? null;
  const powodySumy = podsumowanie
    ? [...new Set(podsumowanie.wiersze.flatMap((w) => (w.kompletny ? [] : w.powody)))]
    : [];
  const calosc = suma ? kosztDoPokazania(suma.usd, suma.kompletna, powodySumy, dane?.kurs ?? null) : null;
  const etykietaWlasciciela = zrodlo === "analizy" ? "Analizy z wywołaniami" : "Próby mikrofonu";

  return (
    <>
      <h1 className="page-title">Koszty</h1>
      <p className="page-lead">
        Szacunkowy koszt wywołań modeli wg cennika, z podziałem na modele i etapy. To nie jest faktura od dostawców.
      </p>

      <Card tytul="Zakres zestawienia">
        <div className="koszty-filtry">
          <SegmentedControl
            etykieta="Okres"
            opcje={OPCJE_OKRESU}
            wartosc={String(dni)}
            onZmiana={(w) => setDni(Number(w) as OkresDni)}
          />
          <SegmentedControl etykieta="Źródło" opcje={OPCJE_ZRODLA} wartosc={zrodlo} onZmiana={setZrodlo} />
        </div>
      </Card>

      {blad !== null && dane === null && !ladowanie ? (
        <Card tytul="Koszt wg modelu">
          <StanBledu
            tytul="Nie udało się wczytać kosztów"
            opis="Spróbuj ponownie za chwilę. Jeśli problem wraca, daj nam znać."
            szczegol={blad}
            akcja={
              <Button wariant="secondary" onClick={odswiez}>
                Spróbuj ponownie
              </Button>
            }
          />
        </Card>
      ) : (
        <>
          <KpiGrid>
            <KpiTile
              etykieta={`Koszt, ostatnie ${dni} dni`}
              wartosc={calosc?.usd ?? null}
              druga={calosc?.pln ?? undefined}
              powodBraku={calosc?.powod ?? undefined}
              ladowanie={dane === null}
            />
            <KpiTile
              etykieta={etykietaWlasciciela}
              wartosc={dane ? formatujLiczbe(dane.liczbaAnaliz) : null}
              ladowanie={dane === null}
            />
            <KpiTile
              etykieta="Wywołania modeli"
              wartosc={suma ? formatujLiczbe(suma.wywolania) : null}
              druga={suma ? `${formatujLiczbe(suma.proby)} ${odmien(suma.proby, "próba", "próby", "prób")}` : undefined}
              ladowanie={dane === null}
            />
            <KpiTile
              etykieta="Tokeny wejścia"
              wartosc={suma && suma.tokenyWejscia !== null ? formatujLiczbe(suma.tokenyWejscia) : null}
              druga={
                suma && suma.tokenyWyjscia !== null
                  ? `${formatujLiczbe(suma.tokenyWyjscia)} ${odmien(suma.tokenyWyjscia, "token", "tokeny", "tokenów")} wyjścia`
                  : undefined
              }
              powodBraku="żaden z modeli w tym okresie nie zapisał zużycia tokenów"
              ladowanie={dane === null}
            />
          </KpiGrid>

          <Card tytul="Koszt wg modelu" akcje={suma && !suma.kompletna ? <Badge wariant="warning">koszt niepełny</Badge> : undefined}>
            {dane === null ? (
              <StanLadowania tekst="Wczytuję koszty..." />
            ) : podsumowanie && podsumowanie.wiersze.length === 0 ? (
              <StanPusty
                tytul="Brak wywołań modeli w tym okresie"
                opis="Zmień okres albo źródło. Koszt pojawi się, gdy powstaną analizy z wywołaniami modeli."
              />
            ) : (
              podsumowanie &&
              suma && (
                <TabelaModeli wiersze={podsumowanie.wiersze} suma={suma} kurs={dane.kurs} opis="Koszt wg modelu" />
              )
            )}
          </Card>

          {podsumowanie && podsumowanie.wgEtapu.length > 0 && dane && (
            <Card tytul="Koszt wg etapu">
              <TabelaEtapow wiersze={podsumowanie.wgEtapu} kurs={dane.kurs} opis="Koszt wg etapu" />
            </Card>
          )}

          {dane && (
            <p className="koszt-podpis koszt-podpis--strona">
              Okres: od {dataPl(dane.okres.od)} do {dataPl(dane.okres.do)}. Szacunek wg cennika z wersji{" "}
              {dane.podsumowanie.wersjaCennika}, nie faktura.{" "}
              {dane.kurs ? opisKursu(dane.kurs) : dane.powodBrakuKursu ? `Kwoty w złotych: brak (${dane.powodBrakuKursu}).` : ""}{" "}
              Nie obejmuje ćwiczeń z rozmówcą: ich wywołania widać w zakładce Developer (filtr Trening).
            </p>
          )}
          {dane && dane.podsumowanie.ostrzezenia.length > 0 && (
            <ul className="koszt-ostrzezenia">
              {dane.podsumowanie.ostrzezenia.map((o) => (
                <li key={o}>{o}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}
